import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ColDef, GridOptions, GridReadyEvent, IDatasource, IGetRowsParams, ITextFilterParams } from 'ag-grid-community';

import { Fleet } from '../../models/fleet.model';
import { Alert } from '../../models/location.model';
import { PublicApiService } from '../../services/public-api.service';
import { FleetSelectorComponent } from '../fleet-selector/fleet-selector.component';

// Make Math available to the template
declare global {
  interface Window {
    Math: typeof Math;
  }
}

@Component({
  selector: 'app-location-alerts',
  templateUrl: './location-alerts.component.html',
  styleUrls: ['./location-alerts.component.scss'],
})
export class LocationAlertsComponent implements OnInit, OnDestroy {
  private dataSource: IDatasource = {
    getRows: (params: IGetRowsParams) => {
      if (!this.selectedFleet) {
        params.failCallback();
        return;
      }

      const startRow = params.startRow;
      const pageSize = params.endRow - params.startRow;
      const page = Math.floor(startRow / pageSize) + 1;
      const filters = JSON.stringify(params.filterModel ?? {});
      const sorters = JSON.stringify(params.sortModel ?? {});
      const request = this.publicApiService.getFleetLocationAlerts(this.selectedFleet.uuid, filters, sorters, page, pageSize);

      request.subscribe({
        next: (response: any) => {
          this.loading = false;
          this.numRows = response.total;
          params.successCallback(response.data, response.total);
        },
        error: (err: any) => {
          console.error('Error loading alerts:', err);
          this.error = err.message;
          params.failCallback();
        },
      });
    },
  };

  // Make Math available to the template
  Math = Math;

  // Reference to the fleet-selector component
  @ViewChild(FleetSelectorComponent) fleetSelector!: FleetSelectorComponent;

  // Fleet data
  selectedFleet: Fleet | null = null;
  selectedAlert: Alert | null = null;
  numRows = 0;
  loading: boolean = false;
  error: string = '';

  // Store reference to event listener for cleanup
  private toggleRowListener!: EventListener;

  // AG Grid
  columnDefs: ColDef[] = [
    {
      field: 'created_at',
      headerName: 'Timestamp',
      sortable: true,
      filter: 'agDateColumnFilter',
      filterParams: {
        filterOptions: ['before', 'after', 'inRange'],
        maxNumConditions: 1,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
      cellRenderer: (data: any) => {
        return data.value ? new Date(data.value).toLocaleDateString() + ' ' + new Date(data.value).toLocaleTimeString() : '';
      },
    },
    {
      field: 'type',
      headerName: 'Type',
      sortable: true,
      filter: true,
      filterParams: {
        filterOptions: [
          'empty',
          {
            displayKey: 'alert',
            displayName: 'Warnings',
            predicate: () => true,
            numberOfInputs: 0,
          },
          {
            displayKey: 'error',
            displayName: 'Errors',
            predicate: () => true,
            numberOfInputs: 0,
          },
        ],
        maxNumConditions: 1,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      } as ITextFilterParams,
      cellRenderer: (data: any) => {
        return `<svg width="24" height="24" 
                 fill="${data.value === 'error' ? 'red' : data.value === 'alert' ? 'orange' : 'white'}"
                 class="bi bi-exclamation-triangle-fill flex-shrink-0 mt-2">
                <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 
                         0L.165 13.233c-.457.778.091 1.767.98 
                         1.767h13.713c.889 0 1.438-.99.98-1.767L8.982
                         1.566zM8 5c.535 0 .954.462.9.995l-.35 
                         3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 
                         0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
            </svg><span>${data.value === 'error' ? 'Error' : data.value === 'alert' ? 'Warning' : ''}</span>`;
      },
    },
    {
      field: 'location.label',
      headerName: 'Location',
      sortable: true,
      filter: true,
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
    },
    {
      field: 'message_type',
      headerName: 'Message Type',
      sortable: true,
      filter: true,
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
    },
    {
      field: 'name',
      headerName: 'Name',
      sortable: true,
      filter: true,
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
    },
  ];

  defaultColDef: ColDef = {
    flex: 1,
    resizable: true,
    wrapText: true,
  };

  gridApi: any;

  // Update grid configuration
  gridOptions: GridOptions = {
    rowModelType: 'infinite',
    cacheBlockSize: 15,
    maxBlocksInCache: 5,
    rowBuffer: 0,
    infiniteInitialRowCount: -1,
    getRowId: params => params.data.uuid,
    datasource: this.dataSource,
    autoSizeStrategy: {
      type: 'fitGridWidth',
      defaultMinWidth: 80,
    },
  };

  constructor(private publicApiService: PublicApiService) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {}

  async onFleetSelected(fleet: Fleet): Promise<void> {
    this.selectedFleet = fleet;
    this.selectedAlert = null;
    if (this.gridApi) {
      this.gridApi.setGridOption('datasource', this.dataSource);
    }
  }

  onModelUpdated() {
    this.loading = false;
    if (this.gridApi) {
      this.gridApi.refreshCells({ force: true });
    }
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridApi.sizeColumnsToFit();
  }

  onRowClicked(event: any): void {
    this.gridApi.deselectAll();
    this.selectedAlert = event.data;
    if (this.gridApi) {
      this.gridApi.forEachNode((node: any) => {
        if (node.data?._id === this.selectedAlert?._id) {
          node.setSelected(true);
        }
      });
      this.gridApi.refreshCells({ force: true });
    }
  }
}
